import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { detectNativeApp } from "@/lib/platform";
import { captureException, addBreadcrumb, setContext } from "@/lib/sentry";
import { useUserCredits } from "@/hooks/useUserCredits";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppSession } from "@/providers/AppSessionProvider";
import { QuickEntryInput } from "@/components/QuickEntryInput";
import { QuickMetricButtons } from "@/components/QuickMetricButtons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Activity, Sparkles, ArrowLeft, ChevronRight, Gift, Copy, Share2, CalendarIcon, Sparkle, LayoutGrid, List, Camera, Type, Pencil, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMetricViewMode } from "@/hooks/useMetricViewMode";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import MobileBottomNav from "@/components/MobileBottomNav";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UndoButton } from "@/components/UndoButton";
import { RedoButton } from "@/components/RedoButton";
import { useFirstTrackTutorial } from "@/stores/tutorialStore";
import { AllEntriesModal } from "@/components/AllEntriesModal";
import { InstallAppButton } from "@/components/InstallAppButton";
import { getMetricsByCategory } from "@/lib/metricDefinitions";
import { BackfillChecklist } from "@/components/BackfillChecklist";
import { useBackfillChecklistStore } from "@/stores/backfillChecklistStore";
import { SubscriptionGate } from "@/components/SubscriptionGate";

const Track = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [showAllEntriesModal, setShowAllEntriesModal] = useState(false);
  const [hasAIInput, setHasAIInput] = useState(false);
  const [isAILoading, setIsAILoading] = useState(false);
  const [swipeOffsetX, setSwipeOffsetX] = useState(0);
  const [hasVisitedUpgrade] = useState(() => localStorage.getItem("hasVisitedUpgrade") === "true");

  // Backfill checklist store
  const backfillChecklist = useBackfillChecklistStore();

  // Swipe gesture refs for day switching (reel effect)
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const isSwipingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const formatDateLabel = useCallback((date: Date) => {
    const today = new Date();
    const isToday = format(date, "MMM d, yyyy") === format(today, "MMM d, yyyy");
    const isDifferentYear = date.getFullYear() !== today.getFullYear();
    if (isToday) return t('track.today');
    if (isDifferentYear) return format(date, "MMM d, ''yy");
    return format(date, "MMM d");
  }, [t]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    isSwipingRef.current = false;
  }, []);

  // Native touchmove handler — attached with { passive: false } so we can
  // call preventDefault() to lock vertical scrolling during a horizontal swipe.
  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;

    const deltaX = e.touches[0].clientX - touchStartXRef.current;
    const deltaY = e.touches[0].clientY - touchStartYRef.current;

    // Once direction is locked, keep swiping; otherwise, lock if horizontal is dominant
    if (!isSwipingRef.current) {
      if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        isSwipingRef.current = true;
      } else if (Math.abs(deltaY) > 10) {
        // Vertical scroll — abort swipe tracking
        touchStartXRef.current = null;
        touchStartYRef.current = null;
        return;
      }
    }

    if (isSwipingRef.current) {
      // Prevent vertical scrolling while swiping horizontally
      e.preventDefault();
      // Apply resistance: offset decays as you drag further
      const maxOffset = 150;
      const clamped = Math.max(-maxOffset, Math.min(maxOffset, deltaX));
      setSwipeOffsetX(clamped);
    }
  }, []);

  // Attach native touchmove with { passive: false } to allow preventDefault
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, [handleTouchMove]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const threshold = 60;
    if (Math.abs(swipeOffsetX) >= threshold) {
      setSelectedDate(prev => {
        const next = new Date(prev);
        next.setDate(next.getDate() + (swipeOffsetX > 0 ? -1 : 1));
        return next;
      });
    }
    setSwipeOffsetX(0);
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    isSwipingRef.current = false;
  }, [swipeOffsetX]);

  // Cache the isPremium value to prevent buttons from being disabled during refetches
  // Start with false as default (free user) instead of undefined to prevent disabled state
  const cachedIsPremiumRef = useRef<boolean>(false);
  const hasSetInitialPremiumRef = useRef<boolean>(false);

  // Use centralized session management
  const { ready, session, isAuthenticated } = useAppSession();
  const user = session?.user || null;

  // Redirect to dashboard on desktop
  useEffect(() => {
    if (ready !== 'ready') return;
    if (isMobile === false) {
      navigate("/", { replace: true });
    }
  }, [ready, isMobile, navigate]);

  // Redirect to auth if not authenticated (only after session is ready)
  useEffect(() => {
    if (ready !== 'ready') return;

    if (!isAuthenticated) {
      console.log("[Track] No session found, redirecting to auth");
      navigate("/auth", { replace: true });
    }
  }, [ready, isAuthenticated, navigate]);

  // Handle opening a specific metric from navigation state
  useEffect(() => {
    const openMetric = (location.state as any)?.openMetric;
    const selectedDateStr = (location.state as any)?.selectedDate;

    // Set the selected date if provided
    if (selectedDateStr) {
      // Parse as local date to avoid timezone issues
      const [year, month, day] = selectedDateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        setSelectedDate(date);
      }
    }

    if (openMetric) {
      // Wait for QuickMetricButtons to mount, then dispatch the event
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-quick-edit", { detail: { metricKey: openMetric } }));
      }, 100);

      // Clear the state so it doesn't retrigger on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Auto-detect when all metrics for a backfill day are logged
  // Watch the query cache for the current selected date
  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
  const { data: currentDayEntry } = useQuery({
    queryKey: ["daily-entry", user?.id, selectedDateStr],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", user.id)
        .eq("entry_date", selectedDateStr)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!user?.id && backfillChecklist.visible,
  });

  // When entry data changes, check if all metrics for this day in the checklist are logged
  useEffect(() => {
    if (!backfillChecklist.visible || !currentDayEntry) return;
    
    const dayInfo = backfillChecklist.days.find(d => d.date === selectedDateStr);
    if (!dayInfo || dayInfo.done) return;

    // Check if all metric keys for this day have non-null values
    const allLogged = dayInfo.metricKeys.every(metricKey => {
      // Convert metric key to column name (same as useBackfillCheck)
      const column = metricKey.startsWith("custom_") && !metricKey.endsWith("_value")
        ? `${metricKey}_value`
        : metricKey;
      return currentDayEntry[column] !== null && currentDayEntry[column] !== undefined;
    });

    if (allLogged) {
      backfillChecklist.autoMarkDayDone(selectedDateStr);
    }
  }, [currentDayEntry, selectedDateStr, backfillChecklist.visible, backfillChecklist.days]);

  // Use centralized profile hook for username check
  const { data: userProfile, isLoading: isLoadingProfile } = useProfile(user?.id);

  // Tutorial management - must come AFTER userProfile is defined
  const { triggerTutorial } = useFirstTrackTutorial(userProfile?.username || null, (key: string) => t(key as any));

  // Only redirect if profile is loaded and has no username
  useEffect(() => {
    if (ready !== 'ready' || !user || isLoadingProfile) return;

    // IMPORTANT: Only redirect if we have a loaded profile that definitely has no username
    // If profile is undefined or null, it might just be loading/refetching - don't redirect
    if (userProfile && userProfile.username === null) {
      console.log("[Track] No username found (username is null), redirecting to auth for onboarding");
      localStorage.removeItem("onboarding_complete");
      navigate("/auth", { replace: true });
    }
  }, [ready, user, userProfile, isLoadingProfile, navigate]);

  const { data: userCredits, isLoading: isLoadingCredits } = useUserCredits(user?.id || null);

  // Fetch metric preferences to check if user has any metrics enabled
  const { data: metricPrefs } = useQuery({
    queryKey: ["metric-preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch custom metrics
  const { data: customMetrics } = useQuery({
    queryKey: ["custom-metrics", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });

  // Fetch metric definitions for building enabled metrics list
  const { data: nutritionMetrics = [] } = useQuery({
    queryKey: ["metrics-nutrition"],
    queryFn: () => getMetricsByCategory('nutrition'),
  });

  const { data: activityMetrics = [] } = useQuery({
    queryKey: ["metrics-activity"],
    queryFn: () => getMetricsByCategory('activity_sleep'),
  });

  const { data: resultsMetrics = [] } = useQuery({
    queryKey: ["metrics-results"],
    queryFn: () => getMetricsByCategory('results'),
  });

  const { data: otherMetrics = [] } = useQuery({
    queryKey: ["metrics-other"],
    queryFn: () => getMetricsByCategory('other'),
  });

  const { data: healthkitMetrics = [] } = useQuery({
    queryKey: ["metrics-healthkit"],
    queryFn: () => getMetricsByCategory('healthkit'),
  });

  // Build enabled metrics list for AllEntriesModal
  const enabledMetricsForModal = useMemo(() => {
    const metrics: Array<{
      key: string;
      name: string;
      emoji: string;
      unit: string;
      isBoolean?: boolean;
    }> = [];

    const metricDefinitions = {
      nutrition: nutritionMetrics,
      activity_sleep: activityMetrics,
      results: [...resultsMetrics, ...otherMetrics],
      healthkit: healthkitMetrics,
    };

    const nutritionPrefs = metricPrefs?.nutrition_metrics ?? [];
    const activityPrefs = metricPrefs?.activity_sleep_metrics ?? [];
    const resultsPrefs = metricPrefs?.results_metrics ?? [];

    // Add built-in metrics
    [...nutritionPrefs, ...activityPrefs, ...resultsPrefs].forEach((key: string) => {
      const def = 
        metricDefinitions.nutrition.find((m) => m.key === key) ||
        metricDefinitions.activity_sleep.find((m) => m.key === key) ||
        metricDefinitions.healthkit.find((m) => m.key === key) ||
        metricDefinitions.results.find((m) => m.key === key);

      if (def) {
        metrics.push({
          key: def.key,
          name: def.name,
          emoji: def.emoji,
          unit: def.unit,
          isBoolean: def.type === 'boolean',
        });
      }
    });

    // Add custom metrics
    if (customMetrics && customMetrics.length > 0) {
      customMetrics.forEach((metric) => {
        metrics.push({
          key: metric.metric_key,
          name: metric.metric_name,
          emoji: metric.metric_emoji,
          unit: metric.metric_unit,
          isBoolean: (metric as any).metric_type === "boolean",
        });
      });
    }

    return metrics;
  }, [metricPrefs, customMetrics, nutritionMetrics, activityMetrics, resultsMetrics, otherMetrics, healthkitMetrics]);

  // Check if user has any metrics enabled (built-in or custom)
  const hasNoBuiltInMetrics = !metricPrefs || (
    (!metricPrefs.nutrition_metrics || metricPrefs.nutrition_metrics.length === 0) &&
    (!metricPrefs.activity_sleep_metrics || metricPrefs.activity_sleep_metrics.length === 0) &&
    (!metricPrefs.results_metrics || metricPrefs.results_metrics.length === 0)
  );
  const hasNoMetrics = hasNoBuiltInMetrics && (!customMetrics || customMetrics.length === 0);

  // Check if user has ever used the AI input feature
  const hasUsedAI = localStorage.getItem('ai_input_used') === 'true';

  // Show "Start here" tooltip if user hasn't used AI input yet
  const shouldShowTryFirstBubble = !hasUsedAI;

  // Sync HealthKit data on track page visit (only once per session)
  useEffect(() => {
    const syncHealthKit = async () => {
      if (!user?.id) return;

      // Check if we've already synced HealthKit in this session
      const hasAlreadySynced = sessionStorage.getItem('healthkit_synced_this_session');
      if (hasAlreadySynced) {
        console.log("[Track] HealthKit already synced this session, skipping");
        return;
      }

      try {
        // Check if running in native app first
        const detection = await detectNativeApp();
        if (!detection.isNative) {
          console.log("[Track] Not in native app, skipping HealthKit sync");
          return;
        }

        // Check if user has HealthKit enabled
        const { data: settings } = await supabase
          .from("healthkit_settings")
          .select("enabled")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!settings?.enabled) return;

        console.log("[Track] HealthKit enabled, syncing today's data...");
        toast.info(t('track.syncing_healthkit'), { duration: 3000 });

        // Dynamically import to avoid loading SDK on non-iOS platforms
        const { syncTodayHealthKitData } = await import("@/lib/healthkit");
        const success = await syncTodayHealthKitData(user.id);

        // Mark as synced for this session
        sessionStorage.setItem('healthkit_synced_this_session', 'true');

        // Get detailed sync results
        const syncResults = (window as any).__healthKitLastSyncResults as Array<{
          metric: string;
          success: boolean;
          error?: string;
          value?: any;
        }> || [];

        if (success) {
          console.log("[Track] HealthKit sync completed");

          const failedMetrics = syncResults.filter(r => !r.success);
          const successMetrics = syncResults.filter(r => r.success);

          if (failedMetrics.length > 0) {
            const failedNames = failedMetrics.map(m => `${m.metric}: ${m.error}`).join(', ');
            if (successMetrics.length > 0) {
              const successDetails = successMetrics.map(m => {
                const value = m.value;
                if (m.metric === 'Steps') return `${m.metric}: ${value} steps`;
                if (m.metric === 'Sleep Time') return `${m.metric}: ${value.toFixed(1)} hrs`;
                return `${m.metric}: ${value}`;
              }).join(', ');
              toast.warning(t('display.synced_metrics', { success: successDetails, failed: failedNames }), { duration: 8000 });
            } else {
              toast.error(t('track.healthkit_all_failed', { details: failedNames }), { duration: 8000 });
            }
          } else if (successMetrics.length > 0) {
            const successDetails = successMetrics.map(m => {
              const value = m.value;
              if (m.metric === 'Steps') return `${m.metric}: ${value} steps`;
              if (m.metric === 'Sleep Time') return `${m.metric}: ${value.toFixed(1)} hrs`;
              return `${m.metric}: ${value}`;
            }).join(', ');
            toast.success(t('track.healthkit_synced', { details: successDetails }), { duration: 8000 });
          }
        } else {
          console.log("[Track] HealthKit sync failed");
          const syncError = (window as any).__healthKitSyncError;
          const failedMetrics = syncResults.filter(r => !r.success);

          let errorMessage = t('track.healthkit_failed');

          if (syncError) {
            errorMessage = `Sync Error: ${syncError}`;
          } else if (failedMetrics.length > 0) {
            const details = failedMetrics.map(m => `${m.metric}: ${m.error}`).join(', ');
            errorMessage = `${t('track.healthkit_failed')}: ${details}`;
          }

          toast.error(errorMessage, { duration: 8000 });
        }
      } catch (e) {
        console.error("[Track] Failed to sync HealthKit:", e);
        captureException(e, {
          tags: { page: 'track', action: 'healthkit-sync' },
          extra: { userId: user?.id },
        });
        toast.error(t('track.healthkit_error', { error: e instanceof Error ? e.message : 'Unknown error' }), { duration: 8000 });
      }
    };

    syncHealthKit();
  }, [user?.id]);

  // Listen for subscription sync events and immediately refetch profile/credits
  useEffect(() => {
    const handleSubscriptionSync = (event: CustomEvent) => {
      if (event.detail.userId === user?.id) {
        console.log("[Track] Subscription synced, refetching profile and credits immediately", {
          userId: event.detail.userId,
          isPremium: event.detail.isPremium
        });
        // CRITICAL FIX: Immediately update the cached premium status from the event
        // This prevents the "progress snaps are a pro feature" error during refetch
        if (typeof event.detail.isPremium === 'boolean') {
          cachedIsPremiumRef.current = event.detail.isPremium;
          hasSetInitialPremiumRef.current = true;
          console.log("[Track] Cache updated from subscription-synced event:", event.detail.isPremium);
        }
        // Force immediate refetch - invalidateQueries marks as stale AND refetches
        queryClient.invalidateQueries({ queryKey: ["user-credits", user.id] });
        queryClient.invalidateQueries({ queryKey: ["subscription-status", user.id] });
        queryClient.invalidateQueries({ queryKey: ["profile", user.id] });
      }
    };

    window.addEventListener("subscription-synced" as any, handleSubscriptionSync);
    return () => {
      window.removeEventListener("subscription-synced" as any, handleSubscriptionSync);
    };
  }, [user?.id, queryClient]);

  // Referral dialog data
  const { data: profile } = useQuery({
    queryKey: ["referral-profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("referral_code, username")
        .eq("id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user && showReferralDialog,
  });

  const { data: referralStats } = useQuery({
    queryKey: ["referral-stats", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("referrals")
        .select("*")
        .eq("referrer_user_id", user!.id)
        .eq("credits_awarded", true);
      if (error) throw error;

      return { completed: data?.length || 0 };
    },
    enabled: !!user && showReferralDialog,
  });

  const referralCode = profile?.referral_code || "";

  // View mode preference hook
  const [viewMode, setViewMode] = useMetricViewMode(user?.id);

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      toast.success(t('track.code_copied'));
    }
  };

  const shareReferralCode = async () => {
    if (!referralCode) return;

    const shareData = {
      title: t('track.share_title'),
      text: t('track.share_text', {
        code: referralCode,
        url: `${window.location.origin}/auth`
      }),
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        copyReferralCode();
      }
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        captureException(err, {
          tags: { page: 'track', action: 'share-referral' },
        });
        copyReferralCode();
      }
    }
  };

  if (ready === 'checking' || !user) {
    return null;
  }

  // Cache the isPremium value to prevent buttons from being disabled during refetches
  // On first successful load, cache the value and mark as initialized
  if (userProfile?.is_premium !== undefined && !hasSetInitialPremiumRef.current) {
    cachedIsPremiumRef.current = userProfile.is_premium || userProfile.is_lifetime_premium || false;
    hasSetInitialPremiumRef.current = true;
    console.log("[Track] Initial premium status cached:", cachedIsPremiumRef.current);
  }

  // Update cache on subsequent loads (after subscription sync, etc.)
  if (userProfile?.is_premium !== undefined && hasSetInitialPremiumRef.current) {
    cachedIsPremiumRef.current = userProfile.is_premium || userProfile.is_lifetime_premium || false;
  }

  // Always use cached value - this prevents buttons from being disabled during refetches
  // The cache is initialized with false (free user) and updated when profile loads
  const effectiveIsPremium = cachedIsPremiumRef.current;

  return (
    <div ref={containerRef} className="min-h-screen bg-background pb-24 relative" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* Subscription gate - blocks non-premium signed-in users */}
      <SubscriptionGate open={!isLoadingProfile && !!userProfile && !effectiveIsPremium} />

      {/* Background gradient effect - only in dark mode */}
      <div className="dark:block hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 2.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 pointer-events-none overflow-hidden will-change-[opacity]"
          style={{ top: '20%' }}
        >
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] h-[80%] bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-blue-600/20 via-pink-500/10 to-transparent blur-[100px]" />
        </motion.div>
      </div>

      <div className="container max-w-2xl mx-auto px-4 relative z-10">
        {/* Header */}
        <header className="py-6 mb-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="grid grid-cols-1 gap-0">
              {/* Date Picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-2xl font-bold text-foreground hover:text-foreground/80 h-auto pt-2 px-2 pb-1 overflow-hidden"
                  >
                    <span className="relative inline-flex items-center overflow-hidden" style={{ minWidth: '6ch' }}>
                      {/* Previous day label */}
                      {(() => {
                        const prevDate = new Date(selectedDate);
                        prevDate.setDate(prevDate.getDate() - 1);
                        // reelWidth: label slides in from the left
                        const reelWidth = 160;
                        const opacity = Math.max(0, Math.min(1, swipeOffsetX / 80));
                        return (
                          <span
                            className="absolute whitespace-nowrap"
                            style={{
                              transform: `translateX(${-reelWidth + swipeOffsetX}px)`,
                              opacity,
                            }}
                            aria-hidden
                          >
                            {formatDateLabel(prevDate)}
                          </span>
                        );
                      })()}
                      {/* Current day label */}
                      <span
                        className="whitespace-nowrap"
                        style={{
                          transform: `translateX(${swipeOffsetX}px)`,
                          opacity: 1 - Math.abs(swipeOffsetX) / 150,
                        }}
                      >
                        {formatDateLabel(selectedDate)}
                      </span>
                      {/* Next day label */}
                      {(() => {
                        const nextDate = new Date(selectedDate);
                        nextDate.setDate(nextDate.getDate() + 1);
                        const reelWidth = 160;
                        const opacity = Math.max(0, Math.min(1, -swipeOffsetX / 80));
                        return (
                          <span
                            className="absolute whitespace-nowrap"
                            style={{
                              transform: `translateX(${reelWidth + swipeOffsetX}px)`,
                              opacity,
                            }}
                            aria-hidden
                          >
                            {formatDateLabel(nextDate)}
                          </span>
                        );
                      })()}
                    </span>
                    <ChevronRight className="ml-2 h-5 w-5 text-muted-foreground/50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const oneYearAgo = new Date(today);
                      oneYearAgo.setDate(today.getDate() - 365);
                      const oneYearForward = new Date(today);
                      oneYearForward.setDate(today.getDate() + 365);
                      return date < oneYearAgo || date > oneYearForward;
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Credits Section - Top Right (always for pro users, otherwise after visiting /upgrade) */}
            {(effectiveIsPremium || hasVisitedUpgrade) && (
              <div className="flex flex-col items-end gap-2 mt-2">
                <button
                  onClick={() => navigate("/credits-success")}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors border border-gray-300/30 rounded-md px-2 py-1"
                >
                  {isLoadingCredits ? (
                    t('track.loading')
                  ) : (
                    <>
                      <Sparkle className="w-3.5 h-3.5" />
                      {userCredits?.credits !== undefined ? (userCredits.credits / 10).toFixed(1) : "0"}
                      <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="space-y-8">
          {/* AI Input Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 mb-4 opacity-100">
              <Sparkles className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('track.track_with_ai')}</span>
            </div>
            <QuickEntryInput
              userId={user.id}
              isPremium={effectiveIsPremium}
              selectedDate={selectedDate}
              showStartHereTooltip={shouldShowTryFirstBubble}
              onAIInputChange={setHasAIInput}
              onAILoadingChange={setIsAILoading}
              onTutorialTrigger={triggerTutorial}
            />
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">{t('track.or_quick_edit')}</span>
            </div>
          </div>

          {/* Quick Edit Buttons Grid */}
          <div className="pb-4 relative">
            {/* All Entries, Undo and View Toggle - positioned above buttons on the right */}
            {!hasAIInput && (
              <div className="absolute -top-10 right-0 flex gap-2 z-10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAllEntriesModal(true)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground opacity-50"
                  title={t('entries.view_all_entries' as any)}
                >
                  <History className="h-4 w-4" />
                </Button>
                <UndoButton userId={user.id} />
                <RedoButton userId={user.id} />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode(viewMode === 'circles' ? 'list' : 'circles')}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground opacity-50"
                >
                  {viewMode === 'circles' ? (
                    <LayoutGrid className="h-4 w-4" />
                  ) : (
                    <List className="h-4 w-4" />
                  )}
                </Button>
              </div>
            )}
            <QuickMetricButtons
              userId={user.id}
              selectedDate={selectedDate}
              layout="grid"
              displayMode={viewMode}
              showAIBubble={shouldShowTryFirstBubble}
              username={userProfile?.username}
            />
          </div>
        </main>
      </div>

      {/* Backfill Checklist Overlay */}
      <BackfillChecklist
        currentDate={format(selectedDate, "yyyy-MM-dd")}
        onNavigateToDate={(date) => {
          const [year, month, day] = date.split('-').map(Number);
          setSelectedDate(new Date(year, month - 1, day));
        }}
      />

      {!isAILoading && <MobileBottomNav />}

      {/* Referral Dialog */}
      <Dialog open={showReferralDialog} onOpenChange={setShowReferralDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Gift className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="break-words">{t('track.invite_friends')}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 overflow-x-hidden">
            {/* How it works */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-sm">{t('track.how_it_works')}</h3>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside break-words">
                <li>{t('track.share_code')}</li>
                <li className="break-words">{t('track.friend_signup')}</li>
                <li>{t('track.friend_metric')}</li>
                <li className="break-words"><strong className="text-foreground">{t('track.both_get_credits')}</strong></li>
              </ol>
            </div>

            {/* Stats */}
            {referralStats && (
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Gift className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <div className="text-3xl font-bold text-green-600">{referralStats.completed}/5</div>
                <div className="text-sm text-muted-foreground">{t('track.completed_referrals')}</div>
              </div>
            )}

            {/* Referral Code */}
            <div className="space-y-2 w-full overflow-hidden">
              <label className="text-sm font-medium">{t('track.your_code')}</label>
              <div className="flex gap-2 w-full">
                <div className="flex-1 bg-muted px-3 py-2 rounded-md text-lg font-mono font-bold text-center overflow-hidden min-w-0">
                  {referralCode || t('track.loading')}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyReferralCode}
                  disabled={!referralCode}
                  className="flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Share Button */}
            <Button
              className="w-full gap-2"
              onClick={shareReferralCode}
              disabled={!referralCode}
            >
              <Share2 className="w-4 h-4" />
              {t('track.share_button')}
            </Button>

            <p className="text-xs text-muted-foreground text-center break-words">
              {t('track.credits_awarded')}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* All Entries Modal */}
      <AllEntriesModal
        open={showAllEntriesModal}
        onOpenChange={setShowAllEntriesModal}
        userId={user.id}
        entryDate={format(selectedDate, "yyyy-MM-dd")}
        enabledMetrics={enabledMetricsForModal}
      />

      <InstallAppButton />
    </div>
  );
};

export default Track;
